import React, { forwardRef } from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helperText?: string;
  error?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  inputSize?: 'sm' | 'md' | 'lg';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      icon,
      iconPosition = 'left',
      inputSize = 'md',
      className = '',
      id,
      required,
      disabled,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `${inputId}-error`;
    const helperTextId = `${inputId}-helper`;

    // Size-based padding — kept compact and consistent with the site's other
    // form inputs (ContactForm, Select) rather than a bulkier SaaS-form scale
    const sizeClasses = {
      sm: 'px-3 py-2 text-xs',
      md: 'px-3.5 py-2.5 text-sm',
      lg: 'px-4 py-3 text-sm',
    };

    // Base input classes — matches the site's flat, sharp-cornered form language
    const baseInputClasses = `
      w-full
      border
      bg-white
      text-[#404040]
      placeholder-[#A3A3A3]
      transition-colors duration-200
      focus:outline-none
      focus:border-[#E85C27]
      disabled:bg-[#F0EDE8]
      disabled:text-[#A3A3A3]
      disabled:cursor-not-allowed
      ${error ? 'border-red-400 focus:border-red-400' : 'border-[#E8E6E2]'}
      ${icon ? (iconPosition === 'left' ? 'pl-10' : 'pr-10') : ''}
      ${sizeClasses[inputSize]}
      ${className}
    `.trim().replace(/\s+/g, ' ');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-xs uppercase tracking-wider text-[#A3A3A3] mb-1.5"
          >
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {icon && (
            <div
              className={`absolute top-1/2 -translate-y-1/2 ${
                iconPosition === 'left' ? 'left-3' : 'right-3'
              } text-[#A3A3A3] pointer-events-none`}
            >
              {icon}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            className={baseInputClasses}
            disabled={disabled}
            required={required}
            aria-invalid={!!error}
            aria-describedby={
              error ? errorId : helperText ? helperTextId : undefined
            }
            {...props}
          />
        </div>

        {error && (
          <p id={errorId} className="text-xs text-red-500 mt-1">
            {error}
          </p>
        )}

        {helperText && !error && (
          <p id={helperTextId} className="text-xs text-[#6B6B6B] mt-1">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
